import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AI Chat Widget powered by GPT-5.2.
 *
 * Props:
 *   persona: "landing" | "member" | "admin"
 *   authToken?: string  (required for member/admin persona)
 *   variant?: "floating" | "inline"  (default: floating)
 *   position?: "bottom-right" | "bottom-left"  (default: bottom-right)
 *   title?: string
 *   welcome?: string
 *   openByDefault?: boolean
 */
export default function ChatWidget({
  persona = "landing",
  authToken = null,
  variant = "floating",
  position = "bottom-right",
  title,
  welcome,
  openByDefault = false,
}) {
  const [isOpen, setIsOpen] = useState(openByDefault);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  const defaultTitles = {
    landing: "Chiedi a Metodo di Mare",
    member: "Coach AI · Area Iscritti",
    admin: "Assistente AI Michele",
  };
  const defaultWelcomes = {
    landing: "Ciao! Sono l'assistente AI di Michele Roccaro. Chiedimi del libro, dei corsi Premium o della certificazione L.4/2013.",
    member: "Ciao! Sono il tuo coach AI. Chiedimi qualsiasi cosa sui contenuti dei corsi.",
    admin: "Pronto. Cosa scriviamo oggi: email outreach, articolo blog, risposta lead?",
  };
  const displayTitle = title || defaultTitles[persona];
  const displayWelcome = welcome || defaultWelcomes[persona];

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: "assistant", text: displayWelcome }]);
    }
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const endpoint = persona === "landing"
    ? `${API}/chat/message`
    : persona === "member" ? `${API}/chat/member` : `${API}/chat/admin`;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const { data } = await axios.post(
        endpoint,
        { message: text, session_id: sessionId, persona },
        { headers },
      );
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply || "..." }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore, riprova più tardi.";
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${msg}` }]);
    } finally {
      setSending(false);
    }
  };

  // Inline variant (embed in page)
  if (variant === "inline") {
    return (
      <div className="bg-white border border-[#E5E1DA] flex flex-col h-[560px] max-h-[70vh]" data-testid={`chat-widget-${persona}`}>
        <ChatHeader title={displayTitle} onClose={null} />
        <ChatMessages messages={messages} sending={sending} bottomRef={bottomRef} />
        <ChatInput input={input} setInput={setInput} send={send} sending={sending} />
      </div>
    );
  }

  // Floating variant (bottom-right or bottom-left bubble)
  const posClass = position === "bottom-left" ? "bottom-4 left-4" : "bottom-4 right-4";
  return (
    <div className={`fixed ${posClass} z-50`} data-testid={`chat-widget-floating-${persona}`}>
      {isOpen ? (
        <div className="bg-white border border-[#E5E1DA] shadow-2xl w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] flex flex-col">
          <ChatHeader title={displayTitle} onClose={() => setIsOpen(false)} />
          <ChatMessages messages={messages} sending={sending} bottomRef={bottomRef} />
          <ChatInput input={input} setInput={setInput} send={send} sending={sending} />
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          data-testid={`chat-open-${persona}`}
          className="group flex items-center gap-2 pl-4 pr-5 py-3 bg-[#0B192C] text-[#F9F8F6] hover:bg-[#E89B6C] hover:text-[#0B192C] shadow-2xl transition-all"
        >
          <MessageCircle size={20} />
          <span className="text-xs uppercase tracking-[0.18em] font-semibold hidden sm:inline">
            Chiedi all&apos;AI
          </span>
        </button>
      )}
    </div>
  );
}

function ChatHeader({ title, onClose }) {
  return (
    <div className="bg-gradient-to-r from-[#0B192C] to-[#173F5F] text-[#F9F8F6] px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[#E89B6C]" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#E89B6C]">AI · GPT-5.2</p>
          <p className="text-sm font-semibold">{title}</p>
        </div>
      </div>
      {onClose && (
        <button onClick={onClose} className="p-1 hover:bg-[#F9F8F6]/10" data-testid="chat-close">
          <X size={16} />
        </button>
      )}
    </div>
  );
}

function ChatMessages({ messages, sending, bottomRef }) {
  return (
    <div className="flex-1 overflow-y-auto p-4 bg-[#F9F8F6] space-y-3" data-testid="chat-messages">
      {messages.map((m, i) => (
        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
          <div
            className={`max-w-[80%] px-3 py-2 text-sm ${
              m.role === "user"
                ? "bg-[#0B192C] text-[#F9F8F6]"
                : "bg-white border border-[#E5E1DA] text-[#0B192C]"
            }`}
            data-testid={`msg-${m.role}`}
          >
            <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
          </div>
        </div>
      ))}
      {sending && (
        <div className="flex justify-start">
          <div className="bg-white border border-[#E5E1DA] px-3 py-2">
            <Loader2 className="animate-spin text-[#0E7C8F]" size={14} />
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

function ChatInput({ input, setInput, send, sending }) {
  return (
    <form onSubmit={send} className="border-t border-[#E5E1DA] p-3 flex items-center gap-2 bg-white">
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Scrivi la tua domanda..."
        disabled={sending}
        data-testid="chat-input"
        className="flex-1 px-3 py-2 border border-[#E5E1DA] text-sm focus:border-[#0E7C8F] outline-none"
      />
      <button
        type="submit"
        disabled={sending || !input.trim()}
        data-testid="chat-send"
        className="p-2 bg-[#E89B6C] text-[#0B192C] hover:bg-[#0B192C] hover:text-[#F9F8F6] disabled:opacity-40 transition-colors"
      >
        <Send size={16} />
      </button>
    </form>
  );
}
